import { useState } from 'react';
import { COPY } from '../content';
import { apiGet, apiPost, type ModerationPost } from '../api';
import { useAsync } from '../hooks';
import { shortDate } from '../format';
import { Spinner, LoadError, Empty, Pill, Sheet } from '../components/ui';

// The two destructive actions go through a confirm sheet that captures a reason; Hide and
// Restore are reversible and act immediately (ADMIN_3 item 4).
type ConfirmAction = { post: ModerationPost; kind: 'remove' | 'ban' };

export function Moderation({ onChange }: { onChange: () => void }) {
  const { data, loading, error, reload } = useAsync(() => apiGet<{ items: ModerationPost[] }>('/v1/admin/posts'), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const items = data?.items ?? [];

  async function moderate(post: ModerationPost, action: 'hide' | 'remove' | 'restore', reason?: string) {
    setBusy(post.id);
    try {
      await apiPost(`/v1/admin/posts/${post.id}/moderate`, reason ? { action, reason } : { action });
      reload();
      onChange();
    } finally {
      setBusy(null);
    }
  }

  async function ban(post: ModerationPost, banned: boolean, reason?: string) {
    setBusy(post.id);
    try {
      await apiPost(`/v1/admin/members/${post.memberId}/ban`, reason ? { banned, reason } : { banned });
      reload();
      onChange();
    } finally {
      setBusy(null);
    }
  }

  async function runConfirm(reason: string) {
    const c = confirm;
    if (!c) return;
    setConfirm(null);
    if (c.kind === 'remove') await moderate(c.post, 'remove', reason);
    else await ban(c.post, true, reason);
  }

  return (
    <div>
      <h1 className="screen-title">{COPY.moderation.title}</h1>
      <p className="screen-sub">{COPY.moderation.sub}</p>

      {loading ? <Spinner /> : null}
      {error ? <LoadError message={error} onRetry={reload} /> : null}
      {data && items.length === 0 ? <Empty>{COPY.moderation.empty}</Empty> : null}

      {items.map((post) => {
        const banned = post.authorAccountStatus === 'suspended';
        return (
          <div className="card" key={post.id}>
            <div className="row between">
              <div className="row" style={{ gap: 8 }}>
                <strong>{post.authorName}</strong>
                {post.authorCrewNumber ? <span className="muted small">#{post.authorCrewNumber}</span> : null}
                {statusTone(post.status)}
                {banned ? <Pill tone="red">Banned</Pill> : null}
              </div>
              <span className="muted small">{shortDate(post.createdAtIso)}</span>
            </div>

            <div className="stack" style={{ marginTop: 8 }}>
              <div className="small muted">
                {post.type} {post.spotName ? `at ${post.spotName}` : ''}
              </div>
              {post.caption ? <div>{post.caption}</div> : <div className="muted small">No caption.</div>}
              <div className="small muted">
                {post.reportCount} reports | {post.commentCount} comments
              </div>
            </div>

            {/* Reversible actions (Hide / Restore / Lift ban) are secondary so the destructive
                Remove / Ban never sit at the same visual weight right beside them. */}
            <div className="toolbar" style={{ marginTop: 12, marginBottom: 0 }}>
              {post.status !== 'hidden' && post.status !== 'removed' ? (
                <button className="secondary" disabled={busy === post.id} onClick={() => moderate(post, 'hide')}>
                  {COPY.moderation.hide}
                </button>
              ) : null}
              {post.status === 'hidden' || post.status === 'removed' || post.status === 'held' ? (
                <button className="secondary" disabled={busy === post.id} onClick={() => moderate(post, 'restore')}>
                  {COPY.moderation.restore}
                </button>
              ) : null}
              {banned ? (
                <button className="secondary" disabled={busy === post.id} onClick={() => ban(post, false)}>
                  {COPY.moderation.unban}
                </button>
              ) : null}
              {post.status !== 'removed' ? (
                <button className="danger" disabled={busy === post.id} onClick={() => setConfirm({ post, kind: 'remove' })}>
                  {COPY.moderation.remove}
                </button>
              ) : null}
              {!banned ? (
                <button className="danger" disabled={busy === post.id} onClick={() => setConfirm({ post, kind: 'ban' })}>
                  {COPY.moderation.ban}
                </button>
              ) : null}
            </div>
          </div>
        );
      })}

      {confirm ? <ConfirmSheet action={confirm} onCancel={() => setConfirm(null)} onConfirm={runConfirm} /> : null}
    </div>
  );
}

function ConfirmSheet({ action, onCancel, onConfirm }: { action: ConfirmAction; onCancel: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  const isBan = action.kind === 'ban';
  const title = isBan ? COPY.moderation.banTitle : COPY.moderation.removeTitle;
  const blurb = isBan ? `${COPY.moderation.banBlurb} ${action.post.authorName}.` : COPY.moderation.removeBlurb;
  const cta = isBan ? COPY.moderation.ban : COPY.moderation.remove;
  return (
    <Sheet title={title} onClose={onCancel}>
      <p className="muted small">{blurb}</p>
      <input placeholder={COPY.moderation.reason} value={reason} onChange={(e) => setReason(e.target.value)} />
      <div className="row" style={{ marginTop: 12 }}>
        <button className="danger" onClick={() => onConfirm(reason.trim())}>
          {cta}
        </button>
        <button className="secondary" onClick={onCancel}>
          {COPY.common.cancel}
        </button>
      </div>
    </Sheet>
  );
}

function statusTone(status: string) {
  if (status === 'held') return <Pill tone="amber">Held</Pill>;
  if (status === 'removed') return <Pill tone="red">Removed</Pill>;
  if (status === 'hidden') return <Pill tone="gray">Hidden</Pill>;
  return <Pill tone="green">Visible</Pill>;
}
